/**
 * @fileoverview The drawers and the "Upload for class" modal that sit on top
 * of the Task Tracker matrix, driven by `useTaskTrackerDrawers`. Shared by the
 * standalone page and the project "Tasks tracker" tab so both behave alike.
 */

import { TeacherFinalAssessmentDrawer } from '../assessmentDrawer/finalAssessmentDrawer'
import { TeacherAssessmentDrawer } from '../assessmentDrawer/teacherAssessmentDrawer'
import { TaskTrackerDeliverableDrawer } from '../deliverableDrawer'
import type { useTaskTrackerDrawers } from '../hooks/useTaskTrackerDrawers'
import { UploadForClassModal } from '../uploadForClass'

interface TaskTrackerOverlaysProps {
  readonly drawers: ReturnType<typeof useTaskTrackerDrawers>
  readonly projectId: string
  readonly projectName: string
  readonly projectFinished: boolean
  /** Reload with the loading state (after an assessment drawer closes). */
  readonly onRefresh: () => void
  /** Reload keeping the table on screen (after uploads). */
  readonly onRefreshSilent: () => void
}

export function TaskTrackerOverlays(props: TaskTrackerOverlaysProps) {
  const { drawers, projectId, projectName, projectFinished, onRefresh, onRefreshSilent } = props

  function closeAssessmentAndRefresh() {
    drawers.closeAssessment()
    onRefresh()
  }

  return (
    <>
      <TeacherAssessmentDrawer
        opened={drawers.assessment.opened}
        sessionMeetingId={drawers.assessment.sessionMeetingId}
        initialStudentId={drawers.assessment.studentId}
        onClose={closeAssessmentAndRefresh}
      />

      <TeacherFinalAssessmentDrawer
        opened={drawers.finalStudentId !== null}
        projectId={projectId}
        projectName={projectName}
        initialStudentId={drawers.finalStudentId}
        onClose={drawers.closeFinal}
        onClosed={onRefresh}
      />

      <TaskTrackerDeliverableDrawer
        key={drawers.deliverable.openToken}
        opened={drawers.deliverable.opened}
        sessionMeetingActivityId={drawers.deliverable.sessionMeetingActivityId}
        studentId={drawers.deliverable.studentId}
        studentFullName={drawers.deliverable.studentFullName}
        projectFinished={projectFinished}
        onClose={drawers.closeDeliverable}
        onChanged={onRefreshSilent}
        onAssess={drawers.assessFromDeliverable}
      />

      <UploadForClassModal
        target={drawers.uploadTarget}
        onClose={drawers.closeUploadForClass}
        onUploaded={onRefreshSilent}
      />
    </>
  )
}
